/**
 * CodeMirrorDSL — a controlled CodeMirror 6 editor for the Forms/Import DSL.
 * Syntax highlighting (a small StreamLanguage), inline diagnostics from the real
 * validator (props.lint → the analyze endpoint), and keyword/widget autocomplete.
 *
 * Props:
 *   value      string             controlled content
 *   onInput    (string) => void   change callback
 *   lint       (string) => Promise<[{code,msg,line,col}]>  async diagnostics
 *   onViewReady (EditorView) => void
 *   height     string             default "100%"
 */
import { onMount, onCleanup, createEffect } from 'solid-js';
import { EditorView, keymap, lineNumbers, highlightActiveLine, drawSelection } from '@codemirror/view';
import { EditorState, Compartment } from '@codemirror/state';
import { StreamLanguage, bracketMatching, indentOnInput } from '@codemirror/language';
import { autocompletion, completionKeymap } from '@codemirror/autocomplete';
import { linter, lintGutter } from '@codemirror/lint';
import { search, searchKeymap } from '@codemirror/search';
import { history, defaultKeymap, historyKeymap, indentWithTab } from '@codemirror/commands';
import { themeBase } from '../lib/theme.js';
import { editorTheme } from '../lib/cmtheme.js';

// Vocabulary (kept in sync with FORMS_DSL_GRAMMAR.md).
const WIDGETS = ['text', 'number', 'date', 'check', 'select', 'table', 'grid', 'connection', 'database', 'target', 'label', 'note'];
const KEYWORDS = ['meta', 'param', 'let', 'section', 'row', 'button', 'validate', 'column', 'search',
  'before', 'after', 'run', 'on', 'require', 'else', 'with',
  'call', 'exec', 'load', 'set', 'for', 'in', 'when', 'if', 'close', 'refresh', 'toast', 'info', 'warn', 'error', 'alert', 'open',
  'insert', 'update', 'delete', 'save', 'import', 'each',
  'is', 'and', 'or', 'not', 'true', 'false', 'null'];
const PROPS = ['label', 'query', 'value', 'display', 'show', 'options', 'default', 'visible', 'enabled', 'format',
  'required', 'min', 'max', 'pattern', 'maxlen', 'edit', 'readonly', 'footer', 'computed', 'key', 'add', 'remove',
  'mode', 'columns', 'of', 'from', 'lookup', 'title', 'width', 'connection', 'database', 'locale', 'type', 'onError', 'transaction', 'source', 'header', 'delimiter', 'sheet', 'confirm', 'role'];

const WIDGETSET = new Set(WIDGETS);
const KEYWORDSET = new Set(KEYWORDS);

// A small stream tokenizer → highlight tags via standard token names.
const dslLanguage = StreamLanguage.define({
  startState: () => ({ triple: false }),
  token(stream, state) {
    if (state.triple) {
      if (stream.match(/.*?"""/)) { state.triple = false; return 'string'; }
      stream.skipToEnd();
      return 'string';
    }
    if (stream.eatSpace()) return null;
    if (stream.match(/#.*/)) return 'comment';
    if (stream.match('"""')) { state.triple = true; return 'string'; }
    if (stream.match(/"(?:[^"\\]|\\.)*"/)) return 'string';
    if (stream.match(/:[A-Za-z_][\w.]*/)) return 'variableName.special'; // :bind
    if (stream.match(/-?\d+(?:\.\d+)?/)) return 'number';
    const w = stream.match(/[A-Za-z_]\w*/);
    if (w) {
      if (KEYWORDSET.has(w[0])) return 'keyword';
      if (WIDGETSET.has(w[0])) return 'typeName';
      return 'variableName';
    }
    if (stream.match(/==|!=|<=|>=|[-+*/<>=.,(){}\[\]]/)) return 'operator';
    stream.next();
    return null;
  },
});

const completions = [
  ...WIDGETS.map((w) => ({ label: w, type: 'type', detail: 'widget' })),
  ...KEYWORDS.map((k) => ({ label: k, type: 'keyword' })),
  ...PROPS.map((p) => ({ label: p, type: 'property' })),
];
const seen = new Set();
const completionList = completions.filter((c) => (seen.has(c.label) ? false : seen.add(c.label)));

function dslComplete(ctx) {
  const word = ctx.matchBefore(/[A-Za-z_]\w*/);
  if (!word || (word.from === word.to && !ctx.explicit)) return null;
  return { from: word.from, options: completionList, validFor: /^[A-Za-z_]\w*$/ };
}

export default function CodeMirrorDSL(props) {
  let host;
  let view;
  const editable = new Compartment();

  const dslLinter = linter(async (v) => {
    if (!props.lint) return [];
    let diags = [];
    try { diags = await props.lint(v.state.doc.toString()); } catch { return []; }
    const out = [];
    const nLines = v.state.doc.lines;
    for (const d of diags || []) {
      const ln = Math.min(Math.max(d.line || 1, 1), nLines);
      const line = v.state.doc.line(ln);
      const from = Math.min(line.from + Math.max((d.col || 1) - 1, 0), line.to);
      out.push({ from, to: line.to > from ? line.to : from + 1, severity: 'error', message: `${d.code}: ${d.msg}` });
    }
    return out;
  }, { delay: 600 });

  onMount(() => {
    view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: props.value || '',
        extensions: [
          lineNumbers(),
          history(),
          drawSelection(),
          highlightActiveLine(),
          bracketMatching(),
          indentOnInput(),
          dslLanguage,
          autocompletion({ override: [dslComplete] }),
          dslLinter,
          lintGutter(),
          search(),
          keymap.of([indentWithTab, ...defaultKeymap, ...historyKeymap, ...completionKeymap, ...searchKeymap]),
          editorTheme(themeBase()),
          // No lineWrapping: long DSL lines (wide option lists, long SQL) scroll
          // horizontally instead of wrapping — the cm-scroller shows an x-scrollbar.
          EditorView.updateListener.of((u) => {
            if (u.docChanged) props.onInput?.(u.state.doc.toString());
          }),
        ],
      }),
    });
    props.onViewReady?.(view);
  });

  // Controlled: push external value changes that didn't originate here.
  createEffect(() => {
    const v = props.value || '';
    if (view && v !== view.state.doc.toString()) {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: v } });
    }
  });

  onCleanup(() => view?.destroy());

  return <div ref={host} class="fdsl-cm" style={{ height: props.height || '100%' }} />;
}
